import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpMethod } from '@core/http/http-method.enum';
import { ErrorService } from '@core/http/error.service';
import { handleError, makeRequest } from '@core/http/resource.utils';
import { NUGGETS_URL } from '@shared/constants/api.constants';
import { COMMENT_PAGE_SIZE } from '@shared/constants/nugget-comment.constants';
import { CommentResponse, CommentsPageResponse } from '@spe-content-store/interfaces';
import { NuggetComment } from '@shared/models/nugget-comment.model';
import { toNuggetComment } from '@shared/utils/nugget-comment.utils';

@Injectable({ providedIn: 'root' })
export class NuggetInteractionService {
    private readonly httpClient = inject(HttpClient);
    private readonly errorService = inject(ErrorService);

    /** Emits the comments oldest first, because the backend returns the newest first. */
    public loadComments(nuggetId: string): Observable<NuggetComment[]> {
        return makeRequest<CommentsPageResponse>(
            this.httpClient,
            HttpMethod.GET,
            this.commentUrl(nuggetId),
            { params: { page: '0', pageSize: String(COMMENT_PAGE_SIZE) } },
            handleError<CommentsPageResponse>('load comments', this.errorService, 'comment')
        ).pipe(map(page => (page?.items ?? []).map(toNuggetComment).reverse()));
    }

    public addComment(nuggetId: string, text: string): Observable<NuggetComment> {
        return makeRequest<CommentResponse>(
            this.httpClient,
            HttpMethod.POST,
            this.commentUrl(nuggetId),
            { body: { text } },
            handleError<CommentResponse>('create comment', this.errorService, 'comment')
        ).pipe(map(toNuggetComment));
    }

    public deleteComment(nuggetId: string, commentId: string): Observable<void> {
        return makeRequest<void>(
            this.httpClient,
            HttpMethod.DELETE,
            `${this.commentUrl(nuggetId)}/${commentId}`,
            {},
            handleError<void>('delete comment', this.errorService, 'comment')
        );
    }

    private commentUrl(nuggetId: string): string {
        return `${NUGGETS_URL}/${nuggetId}/comments`;
    }
}
